import { createReducer } from 'redux-create-reducer'
import type { Artist, ArtistId } from 'types/artist'
import type { Playlist } from 'types/playlist'
import {
  ARTIST_LOAD,
  ARTIST_LOAD_FAIL,
  ARTIST_LOAD_SUCCESS,
  ARTIST_SHOWCASES_LOAD_SUCCESS,
  ARTIST_TRACKS_LOAD,
  ARTIST_TRACKS_LOAD_FAIL,
  ARTIST_TRACKS_LOAD_SUCCESS,
  ARTIST_UPDATE_SUCCESS,
  ARTISTS_LOAD,
  ARTISTS_LOAD_FAIL,
  ARTISTS_LOAD_NEXT,
  ARTISTS_LOAD_NEXT_FAIL,
  ARTISTS_LOAD_NEXT_SUCCESS,
  ARTISTS_LOAD_RELATED_SUCCESS,
  ARTISTS_LOAD_SUCCESS,
  CLIENT_LIBRARY_ARTIST_LOAD_SUCCESS,
  CLIENT_LIBRARY_ARTIST_UPDATE,
  CLIENT_LIBRARY_ARTIST_UPDATE_FAIL,
  CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_ARTISTS_LOAD_SUCCESS,
  DATA_LOAD,
  PLAYLIST_LOAD_BATCH,
} from '@disco/constants/ActionTypes'
import { omit } from '@disco/utils/array'
export type State = {
  error: Error | null | undefined
  invalidate: boolean
  loaded: boolean
  loading: boolean
  nextPage: string | null | undefined
  items: Record<ArtistId, Artist>
  list: ArtistId[]
}
const initialState: State = {
  error: null,
  invalidate: false,
  loaded: false,
  loading: false,
  nextPage: null,
  items: {},
  list: [],
}
export const artistsReducer = createReducer<State, any>(initialState, {
  [DATA_LOAD](state, { data }) {
    if (!data.artists || !data.artists.length) {
      return state
    }

    const items = { ...state.items }
    data.artists.forEach(item => {
      items[item.id] = { ...items[item.id], ...item }
    })
    return { ...state, items }
  },

  [ARTISTS_LOAD](state) {
    return {
      ...state,
      error: null,
      invalidate: false,
      loading: true,
      nextPage: null,
      list: [],
    }
  },

  [ARTISTS_LOAD_SUCCESS](state, { response }) {
    const items = { ...state.items }
    response.results.forEach(item => {
      items[item.id] = { ...items[item.id], ...item }
    })
    return {
      ...state,
      error: null,
      invalidate: false,
      loaded: true,
      loading: false,
      nextPage: response.next,
      items,
      list: response.results.map(artist => artist.id),
    }
  },

  [ARTISTS_LOAD_FAIL](state, { error }) {
    return { ...state, error, loading: false }
  },

  [ARTISTS_LOAD_NEXT](state) {
    return { ...state, error: null, loading: true }
  },

  [ARTISTS_LOAD_NEXT_SUCCESS](state, { response }) {
    const items = { ...state.items }
    response.results.forEach(item => {
      items[item.id] = { ...items[item.id], ...item }
    })
    return {
      ...state,
      error: null,
      invalidate: false,
      loaded: true,
      loading: false,
      nextPage: response.next,
      items,
      list: [...state.list, ...response.results.map(artist => artist.id)],
    }
  },

  [ARTISTS_LOAD_NEXT_FAIL](state, { error }) {
    return { ...state, error, loading: false }
  },

  [ARTISTS_LOAD_RELATED_SUCCESS](state, { response }) {
    const items = { ...state.items }
    response.results.forEach(item => {
      items[item.id] = { ...items[item.id], ...item }
    })
    return { ...state, items }
  },

  [ARTIST_LOAD](state, { artistId }) {
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: {
          ...(state.items[artistId] ?? {}),
          loading: true,
        } as Artist,
      },
    }
  },

  [ARTIST_LOAD_SUCCESS](state, { artistId, response }) {
    if (!state.items[artistId]) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: { ...state.items[artistId], ...response, loading: false },
      },
    }
  },

  [ARTIST_LOAD_FAIL](state, { artistId }) {
    if (!state.items[artistId]) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: { ...state.items[artistId], loading: false },
      },
    }
  },

  [ARTIST_SHOWCASES_LOAD_SUCCESS](state, { artistId, response }) {
    if (!state.items[artistId]) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: {
          ...state.items[artistId],
          showcase_playlist_ids: response.results.map(
            playlist => playlist.playlist_id,
          ),
        },
      },
    }
  },

  [ARTIST_TRACKS_LOAD](state, { artistId, next }) {
    const artist = state.items[artistId]
    if (!artist) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: {
          ...artist,
          tracks: {
            error: null,
            loading: true,
            list: next ? artist.tracks?.list ?? [] : [],
            next: next || null,
          },
        },
      },
    }
  },

  [ARTIST_TRACKS_LOAD_FAIL](state, { artistId, error }) {
    const artist = state.items[artistId]
    if (!artist) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: {
          ...artist,
          tracks: {
            list: [],
            next: null,
            ...artist.tracks,
            error,
            loading: false,
          },
        },
      },
    }
  },

  [ARTIST_TRACKS_LOAD_SUCCESS](state, { artistId, list, next }) {
    if (!state.items[artistId]) return state
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: {
          ...state.items[artistId],
          tracks: {
            ...state.items[artistId].tracks,
            list: [...(state.items[artistId].tracks?.list ?? []), ...list],
            loading: false,
            next,
            error: null,
          },
        },
      },
    }
  },

  [ARTIST_UPDATE_SUCCESS](state, { artistId, response }) {
    return {
      ...state,
      invalidate: true,
      items: {
        ...state.items,
        [artistId]: { ...state.items[artistId], ...response },
      },
    }
  },

  [CLIENT_LIBRARY_ARTISTS_LOAD_SUCCESS](state, { response }) {
    const items = { ...state.items }
    response.results
      .map(item => omit(item, ['is_featured', 'tracks']))
      .forEach(item => {
        items[item.id] = { ...items[item.id], ...item }
      })
    return { ...state, invalidate: false, items }
  },

  [CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_SUCCESS](state, { response }) {
    const items = { ...state.items }
    response.results
      .map(item => omit(item, ['is_featured', 'tracks']))
      .forEach(item => {
        items[item.id] = { ...items[item.id], ...item }
      })
    return { ...state, invalidate: false, items }
  },

  [CLIENT_LIBRARY_ARTIST_LOAD_SUCCESS](state, { response }) {
    return {
      ...state,
      items: {
        ...state.items,
        [response.id]: {
          ...state.items[response.id],
          ...omit(response, ['is_featured', 'tracks']),
        },
      },
    }
  },

  [CLIENT_LIBRARY_ARTIST_UPDATE](
    state: State,
    { artistId, data }: Record<string, any>,
  ) {
    return {
      ...state,
      items: {
        ...state.items,
        [artistId]: { ...state.items[artistId], ...data },
      },
    }
  },

  [CLIENT_LIBRARY_ARTIST_UPDATE_FAIL](
    state: State,
    { artistId, artist }: Record<string, any>,
  ) {
    return { ...state, items: { ...state.items, [artistId]: artist } }
  },

  [PLAYLIST_LOAD_BATCH](
    state: State,
    {
      list,
    }: {
      list: Playlist[]
    },
  ) {
    const updateItems = {}
    list.forEach((playlist: Playlist) => {
      const artistId = playlist?.showcase?.artist?.id
      if (!artistId) return
      const currentArtist = artistId && state.items[artistId]
      if (!currentArtist) return
      const needUpdate = !currentArtist.showcase_playlist_ids?.includes(
        playlist.playlist_id,
      )
      if (!needUpdate) return
      updateItems[artistId] = {
        ...currentArtist,
        showcase_playlist_ids: [
          playlist.playlist_id,
          ...(currentArtist.showcase_playlist_ids || []),
        ],
      }
    })
    return { ...state, items: { ...state.items, ...updateItems } }
  },
})
